import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'
import 'components/shared/product-preview/product-preview.js'
import 'components/ordering/order-form/components/order-form.js'
import { style } from './pdp-sticky-bar.style.js'
import 'components/discovery/pdp/provider.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'


initializeIconsByRole({
  'csc-order-form.initial': { name: 'shopping-cart', style: 'regular' },
})

@customElement('csc-pdp-sticky-bar')
export class CSCPdpStickyBar extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: String })
  position: 'top' | 'bottom' = 'bottom'

  @property({ type: String, attribute: 'trigger-selector' })
  triggerSelector = ''

  @state()
  private product: PdpProduct | null = null

  @state()
  private isMobile = false

  @state()
  private triggerScrolledPast = false

  private mediaQuery: MediaQueryList | undefined

  private triggerObserver: IntersectionObserver | undefined

  private observeTrigger = () => {
    this.triggerObserver?.disconnect()
    if (!this.triggerSelector) return

    const trigger = document.querySelector(this.triggerSelector)
    if (!trigger) return

    this.triggerObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return
      this.triggerScrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0
    })
    this.triggerObserver.observe(trigger)
  }

  private onMediaChange = (e: MediaQueryListEvent) => {
    this.isMobile = e.matches
  }

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product() as PdpProduct | null
      if (product) {
        this.product = product
      }
    })
  }

  override connectedCallback() {
    super.connectedCallback()
    this.mediaQuery = window.matchMedia(`(max-width: ${breakpoints.medium - 1}px)`)
    this.isMobile = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.onMediaChange)

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.observeTrigger, { once: true })
    } else {
      this.observeTrigger()
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.mediaQuery?.removeEventListener('change', this.onMediaChange)
    document.removeEventListener('DOMContentLoaded', this.observeTrigger)
    this.triggerObserver?.disconnect()
  }

  private get isShown() {
    return !this.triggerSelector || this.triggerScrolledPast
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('triggerSelector') && changedProperties.get('triggerSelector') !== undefined) {
      this.observeTrigger()
    }
    this.toggleAttribute('visible', !!this.product && this.isShown)
  }

  override render() {
    if (!this.product) return nothing
    const product = this.product

    return html`
      <cse-sticky-bar position=${this.position} ?inert=${!this.isShown}>
        <csc-pdp-provider>
          <div class=${classMap({ 'sticky-bar-content': true, mobile: this.isMobile })}>
            <csc-product-preview
              class="sticky-bar-preview"
              size="small"
              product-name="${product.name}"
              product-id="${product.code}"
              image-source="${product.picture.small}"
              fallback-image-source="${product.default_picture.small}"
              page-link="${product.page_link}"
            ></csc-product-preview>
            <div class="order-section">
              <slot name="price"></slot>
              <csc-order-form
                product-id="${product.id}"
                min="${ifDefined(product.ordering.minimum_order_quantity ?? undefined)}"
                max="${ifDefined(product.ordering.maximum_order_quantity ?? undefined)}"
                step="${product.ordering.order_quantity ?? 1}"
                default="${product.ordering.order_quantity ?? 1}"
                ?show-button-label=${true}
                ?cannot-be-added-to-basket=${!!product.ordering.cannot_be_added_to_basket}
              >
                <slot name="uom" slot="uom"></slot>
              </csc-order-form>
            </div>
          </div>
        </csc-pdp-provider>
      </cse-sticky-bar>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-pdp-sticky-bar': CSCPdpStickyBar
  }
}
